import { getInstanceDefinition } from '../cards/cardLookup';
import { computeRemainingGold, getPlayerById } from '../state/selectors';
import { performLevelUp } from '../village/leveling';

// Extra-buy abilities (e.g. Bounty Hunter's Spoils, Smuggler's React) call
// performBuy() directly rather than going through this cap - only the
// player's default once-per-Village-visit buy (plus any bonusBuys granted by
// an ability) is capped here. Exported so PlayerDock.vue's own "buys left"
// display/auto-end-turn check (see its own comment) uses this exact same
// formula rather than a second, easily-drifted copy of it - see the bug that
// happened when it didn't (Matt: "I click and I'm able to buy one filigree
// amulet but then the turn gets auto ended").
export function buysAllowedThisVisit(player) {
  return 1 + (player.turnFlags.bonusBuys || 0);
}

function getStackArray(state, source) {
  if (source.type === 'hero') return state.village.heroStacks[source.key];
  if (source.type === 'village') return state.village.villageStacks[source.key];
  if (source.type === 'basic') return state.village.basicStacks[source.key];
  throw new Error(`Unknown buy source type: ${source.type}`);
}

function setStackArray(village, source, newStack) {
  if (source.type === 'hero') return { ...village, heroStacks: { ...village.heroStacks, [source.key]: newStack } };
  if (source.type === 'village') return { ...village, villageStacks: { ...village.villageStacks, [source.key]: newStack } };
  return { ...village, basicStacks: { ...village.basicStacks, [source.key]: newStack } };
}

// Core purchase mechanic: buy whatever's on top of `source`'s stack, paying
// with the live-recalculated hand gold value (rulebook: "can only buy the top
// card of a stack"). Does not touch buysUsed - callers decide whether this
// purchase counts against the default 1-buy cap (buyCard) or is an
// independent grant from a card ability (e.g. Spoils: Buy 1 village card).
export function performBuy(state, playerId, source, { requireGoldCheck = true, destination = 'discard' } = {}) {
  const stack = getStackArray(state, source);
  if (!stack || stack.length === 0) throw new Error('That stack is empty.');
  const topInstanceId = stack[0];
  const def = getInstanceDefinition(state, topInstanceId);
  // Root of Corruption, Discord Incarnate: "Trophy: Cards cost 1 extra
  // gold." Trophy effects "must be used every time they are present in
  // your hand" (rulebook, "Trophies") - i.e. a per-turn condition tied to
  // hand presence, re-applied automatically whenever the card's Trophy
  // resolves (abilityActions.js's resolveTrophyEffects, on VISIT_VILLAGE/
  // ENTER_DUNGEON), not a one-shot permanent effect. Implemented as a
  // turnFlag (grantExtraGoldCostPerCard, resets every turn like any other
  // turnFlag) rather than a persistent village-level modifier - only
  // applies to cost actually being checked/deducted, so free buys
  // (requireGoldCheck: false) are correctly unaffected.
  const extraCostPerCard = state.players.find((p) => p.id === playerId)?.turnFlags.extraGoldCostPerCard || 0;
  const effectiveCost = (def.cost || 0) + extraCostPerCard;
  if (requireGoldCheck) {
    const goldAvailable = computeRemainingGold(state, playerId);
    if (goldAvailable < effectiveCost) throw new Error(`Not enough gold (need ${effectiveCost}, have ${goldAvailable}).`);
  }
  let village = setStackArray(state.village, source, stack.slice(1));
  // Numenera, Paired Swords: "When you buy this card, you may place another
  // copy of it from the village into your discard pile." Modeled as always
  // taking the free copy when one remains (same "you may defaults to
  // accepting" convention used elsewhere) - into discard specifically,
  // regardless of this purchase's own `destination`.
  let bonusCopyInstanceId = null;
  if (def.id === 'village-paired-swords' && source.type === 'village') {
    const remaining = village.villageStacks[source.key];
    if (remaining && remaining.length) {
      bonusCopyInstanceId = remaining[0];
      village = setStackArray(village, source, remaining.slice(1));
    }
  }
  // Starter set, Blackhand Smuggler: "If you purchase a hero or villager
  // this turn, you may place it on top of your deck instead of in your
  // discard pile." Only overrides the DEFAULT discard destination - an
  // explicit non-discard destination (e.g. Rapparee Looter's own "place on
  // top of your deck" ability) always wins, so this never fights another
  // card's own more specific placement. "You may" defaults to accepting,
  // same convention as every other "you may" ability.
  const isBuyerType = def.type === 'hero' || def.traits.includes('Villager');
  const hasBlackhand = state.players.find((p) => p.id === playerId)?.hand.some((id) => getInstanceDefinition(state, id).id === 'hero-blackhand-l1');
  const effectiveDestination = destination === 'discard' && isBuyerType && hasBlackhand ? 'deck' : destination;
  const players = state.players.map((p) => {
    if (p.id !== playerId) return p;
    // Filigree Amulet: "You may buy a second card when you purchase this
    // card." - the only ToR card with an on-purchase trigger, so special-
    // cased directly here rather than building a generic per-card purchase
    // hook for one card (same convention as e.g. Belac/Smuggler being
    // special-cased in combat/resolveCombat.js - see
    // effectRegistryCoverage.test.js's SPECIAL_CASE_ALWAYS_ACTIVE). Modeled
    // as the same turnFlags.bonusBuys a bonus-buy-granting ability already
    // uses (Innkeeper, two familiar abilities) - "may" is satisfied by a
    // bonus buy simply being optional to spend, same as any other one.
    // Fires regardless of how the purchase happened (buyCard's normal cap,
    // or an ability-granted free buy via performBuy directly) since the
    // printed text isn't conditioned on that.
    // Into the Abyss, Icon of Inversion: "When you buy this card, you may
    // buy one additional card." Identical on-purchase-trigger shape to
    // Filigree Amulet's own bonus buy above.
    const bonusBuys = (def.id === 'village-filigree-amulet' || def.id === 'village-icon-of-inversion')
      ? p.turnFlags.bonusBuys + 1
      : p.turnFlags.bonusBuys;
    const goldSpentThisVisit = requireGoldCheck
      ? p.turnFlags.goldSpentThisVisit + effectiveCost
      : p.turnFlags.goldSpentThisVisit;
    return {
      ...p,
      // Caverns of Bane, Rapparee Looter: "Buy 1 village card. Place it on
      // top of your deck." Root of Corruption, Armsman: "Buy 1 weapon and
      // place it into your hand." / "Place 1 weapon from a village stack
      // into your hand." - the destination option's 3rd case.
      discard: [
        ...p.discard,
        ...(effectiveDestination === 'discard' ? [topInstanceId] : []),
        ...(bonusCopyInstanceId ? [bonusCopyInstanceId] : []),
      ],
      deck: effectiveDestination === 'deck' ? [topInstanceId, ...p.deck] : p.deck,
      hand: effectiveDestination === 'hand' ? [...p.hand, topInstanceId] : p.hand,
      turnFlags: { ...p.turnFlags, bonusBuys, goldSpentThisVisit },
      // requireGoldCheck gates the surcharge here too, matching
      // goldSpentThisVisit above - a free buy (Bounty Hunter's Spoils,
      // Smuggler's React, etc.) already counted its printed cost toward
      // this stat unconditionally before this change; Discord Incarnate's
      // extra gold was never actually spent on a free buy, so it doesn't
      // apply there either.
      stats: { ...p.stats, cardsBought: p.stats.cardsBought + 1, goldSpent: p.stats.goldSpent + (def.cost || 0) + (requireGoldCheck ? extraCostPerCard : 0) },
    };
  });
  return { ...state, players, village };
}

export function buyCard(state, { playerId, source }) {
  const player = getPlayerById(state, playerId);
  if (player.turnFlags.buysUsed >= buysAllowedThisVisit(player)) {
    throw new Error('No buys remaining this turn.');
  }
  const nextState = performBuy(state, playerId, source);
  return {
    ...nextState,
    players: nextState.players.map((p) => (p.id === playerId
      ? { ...p, turnFlags: { ...p.turnFlags, buysUsed: p.turnFlags.buysUsed + 1 } }
      : p)),
  };
}

// Destroys `heroInstanceId` (must be in hand), pays its printed XP cost, and
// moves the matching next-level copy from the village into the player's
// discard pile (rulebook: leveling never returns the new copy to hand by
// default). Cannot skip levels; cannot level the same hero group twice in one
// turn (tracked by group identity, not instance id - see the rulebook's
// equip-then-level worked example; enforced by performLevelUp itself so
// every caller - this, and the bespoke Whetmage/Veteran Trainer/Necrophidius
// effects - gets it consistently). targetInstanceId selects which level-1
// hero to become when heroInstanceId is a Regular (no group of its own) -
// see performLevelUp and getRegularLevelUpCandidates.
// choices.useSeedbombInstanceId: real player choice (Village-timing, same
// "choices resolved before dispatch" convention as every other card-driven
// choice) - opt in to Seedbomb's own React ("destroy this card to put the
// lower-level hero in your discard pile instead of destroying it") by
// naming which Seedbomb copy in hand to spend. Omitted entirely (the
// default) means the old card is destroyed as normal - performLevelUp
// itself validates the id if one is supplied.
// choices.xpTokenColors: bug report -Oxqycc8OLie0qvxFSJ2 (Matt): "the user
// ought to be able to select which XP pieces they want to discard" - an
// array of exactly as many colors as this level-up's XP cost, in the order
// to discard them (see effectHelpers.js's discardXpTokens). Same "resolved
// before dispatch" convention as useSeedbombInstanceId just above - the
// player's own available colors and the exact cost are both already known
// before this dispatches, so there's no need for a post-hoc pending-choice
// marker the way an outcome-dependent choice (e.g. Caldera Dragon's discard)
// would need. Omitted entirely (the default) falls back to the old
// auto-pick-in-fixed-color-order behavior.
export function levelUpHero(state, {
  playerId, heroInstanceId, targetInstanceId, choices,
}) {
  const player = getPlayerById(state, playerId);
  if (!player.hand.includes(heroInstanceId)) throw new Error('That hero is not in your hand.');
  return performLevelUp(state, playerId, heroInstanceId, {
    payXp: true,
    destination: 'discard',
    targetInstanceId,
    useSeedbombInstanceId: choices?.useSeedbombInstanceId,
    xpTokenColors: choices?.xpTokenColors,
  });
}
